import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTypes, Button, Drawer, Icon, useDrawer } from '@/components/shared';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { getListApplicationRegistrationsGroupedQueryKey } from '@/generated/application-registrations';
import {
	AccountId,
	type FilterUnitRegistrationRequest,
	PageType,
	RegistrationCompositeId,
} from '@/generated/data.schemas';
import { useCreateFilterRegistration } from '@/generated/filter-registration';
import { useCreateCompositeWithFilterRegistration } from '@/generated/filter-registration-endpoints';
import { useGetLayout } from '@/generated/layout';
import { getGetRegistrationCompositeQueryKey } from '@/generated/registration-composite';
import { useGetTranslations } from '@/generated/translations';
import { isHttpValidationProblemDetails } from '@/helpers';
import { useNotificationStore } from '@/store/notificationStore';
import styles from './FilterUnit.module.scss';
import { FilterUnitForm } from './shared/FilterUnitForm';

interface RegisterFilterUnitProps {
	ariaControlId: string;
	accountId: AccountId;
	registrationCompositeId?: RegistrationCompositeId;
}

export const RegisterFilterUnit: React.FunctionComponent<RegisterFilterUnitProps> = ({
	ariaControlId,
	accountId,
	registrationCompositeId,
}) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);
	const { data: layout } = useGetLayout(segmentationId);

	const addNotification = useNotificationStore((state) => state.addNotification);
	const queryClient = useQueryClient();

	const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
	const [hasFormChanged, setHasFormChanged] = useState(false);

	const formId = 'register-filerUnit-form'; // used for binding form and submit button

	const drawerRef = useRef<HTMLDivElement>(null);
	const { isOpen, isRendered, handleOpen, handleClose } = useDrawer(drawerRef);

	const path = useMemo(() => {
		return layout?.data.staticLinks.find((link) => link.pageType === PageType.RegistrationComposite)?.url;
	}, [layout]);

	const mutationOptions = {
		onSuccess: async (x) => {
			// refresh groups list to show filter unit name in list
			await queryClient.invalidateQueries({
				queryKey: getListApplicationRegistrationsGroupedQueryKey(segmentationId, accountId),
			});

			addNotification({
				children: translations?.data.filterRegistrations.saveSuccess,
				type: AlertTypes.SUCCESS,
			});

			if (!registrationCompositeId) {
				window.location.href = `${path}?accountid=${accountId}&registrationcompositeid=${x.data}`;
			} else {
				await queryClient.invalidateQueries({
					queryKey: getGetRegistrationCompositeQueryKey(segmentationId, accountId, registrationCompositeId),
				});
			}

			handleClose();
		},
		onError: (axiosError) => {
			const error = axiosError.response?.data;
			if (!error) {
				console.error('Unknown error', axiosError);
			} else if (isHttpValidationProblemDetails(error)) {
				addNotification({
					children: translations?.data.filterRegistrations.saveFailed,
					type: AlertTypes.DANGER,
				});

				setValidationErrors(error.errors);
			} else {
				addNotification({
					children: translations?.data.shared.genericErrorMsg,
					type: AlertTypes.DANGER,
				});
			}
		},
	};

	const createCompositeWithFilterUnit = useCreateCompositeWithFilterRegistration({
		mutation: mutationOptions,
	});

	const createFilterRegistrationMutation = useCreateFilterRegistration({
		mutation: mutationOptions,
	});

	const openDrawer = useCallback((): void => {
		setHasFormChanged(false);
		handleOpen();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// set has input text changed state - used for disabling the save button
	const handleChange = (): void => {
		if (hasFormChanged) return;
		setHasFormChanged(true);
	};

	const handleSubmit = (payload: FilterUnitRegistrationRequest) => {
		setValidationErrors(null);
		if (!registrationCompositeId) {
			createCompositeWithFilterUnit.mutate({
				segmentationId,
				accountId,
				data: payload,
			});
		} else {
			createFilterRegistrationMutation.mutate({
				segmentationId,
				accountId,
				registrationCompositeId,
				data: payload,
			});
		}
	};

	return (
		<>
			{/* Trigger button */}
			<Button
				variant={'ghost'}
				onClick={openDrawer}
				aria-expanded={isOpen}
				aria-controls={ariaControlId}
				aria-label={translations?.data.filterRegistrations.addFilterUnit}
			>
				<Icon name={'plus'} size={'sm'} />
			</Button>

			{isRendered && (
				<Drawer
					drawerRef={drawerRef}
					isOpen={isOpen}
					id={ariaControlId}
					desktopWidth={'400px'}
					direction={'Right'}
				>
					<Drawer.Header
						title={translations?.data.filterRegistrations.addFilterUnit}
						handleClose={handleClose}
					/>

					<Drawer.Body>
						<div className={styles.innerBody}>
							<FilterUnitForm
								formId={formId}
								legend={translations?.data.filterRegistrations.addFilterUnit}
								validationErrors={validationErrors}
								handleSubmit={handleSubmit}
								handleChange={handleChange}
								accountId={accountId}
							/>
						</div>
					</Drawer.Body>

					<Drawer.Footer className={styles.footer} hasNoPadding>
						<Button variant={'ghost'} isFullWidth onClick={handleClose}>
							{translations?.data.shared.cancel}
						</Button>
						<Button type="submit" form={formId} variant={'primary'} isFullWidth disabled={!hasFormChanged}>
							{translations?.data.filterRegistrations.save}
						</Button>
					</Drawer.Footer>
				</Drawer>
			)}
		</>
	);
};
